import React, { useEffect, useState } from 'react';

import { connect } from 'react-redux';
import { withRouter } from 'react-router';

import { useFormik } from 'formik';
import * as Yup from 'yup';
import queryString from 'query-string';

import clsx from 'clsx';

import { get, isEmpty } from 'lodash';

import PlacesAutocomplete, { geocodeByAddress, getLatLng } from 'react-places-autocomplete';

import InputAdornment from '@material-ui/core/InputAdornment';
import CircularProgress from '@material-ui/core/CircularProgress';
import Checkbox from '@material-ui/core/Checkbox';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';
import GoogleLogo from '../../../../components/GoogleLogo';
import { TimezoneOffsets } from '../../../../helpers/timezones';
import BobLoadingMask from '../../../../components/BobLoadingMask';
import BobClientSelector from '../../../../components/BobClientSelector';
import AutoComplete from '@material-ui/lab/Autocomplete';
import SiteTabContainer from './SiteTabContainer';

import BobConfirmButton from '../../../../components/BobConfirmButton';
import BobCancelButton from '../../../../components/BobCancelButton';
import AddNewCampusModal from '../../Modals/AddNewCampusModal';

import * as K2ACoreActions from '../../../../actions/k2aCoreActions';
import * as ArchitectActions from '../../../../actions/architectActions';
import * as ContactActions from '../../../../actions/contactActions';

import * as classes from './ManageSitePage.module.css';

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

const ValidationSchema = Yup.object().shape({
    client: Yup.string()
        .max(45)
        .required(),
    name: Yup.string()
        .max(45)
        .required(),
    buildingType: Yup.string()
        .required(),
    squareFootage: Yup.number()
        .required(),
    fuelTypes:         Yup.array(),
    salesContact:      Yup.string(),
    facilitiesContact: Yup.string(),
    energyContact:     Yup.string(),
    energySite:        Yup.string(),
    squareAreaUOM:     Yup.string(),
    timeZone:          Yup.string().required('This field is required.').nullable(),
    addressSearch:     Yup.string()
        .required(),
    campus:      Yup.string(),
    energyPrice: Yup.lazy(() => Yup.number()
        .when(['energyEmission', 'energyTargetValue'], {
            is:   (energyEmission, energyTargetValue) => energyEmission || energyTargetValue,
            then: Yup.number().required()
        })),
    energyPriceUnit: Yup.string(),
    energyEmission:  Yup.lazy(() => Yup.number()
        .when(['energyPrice', 'energyTargetValue'], {
            is:   (energyPrice, energyTargetValue) => energyPrice || energyTargetValue,
            then: Yup.number().required()
        })),
    energyEmissionUnit: Yup.string(),
    energyTargetValue:  Yup.lazy(() => Yup.string()
        .when(['energyEmission', 'energyPrice'], {
            is:   (energyEmission, energyPrice) => energyPrice || energyEmission,
            then: Yup.string().required()
        }))
}, [
    ['energyEmission', 'energyTargetValue'],
    ['energyPrice', 'energyTargetValue'],
    ['energyEmission', 'energyPrice']
]);

const ManageSitePage = ({
    activeNavCompany,
    activeNavClient,
    buildingTypes,
    contacts,
    createCampusSite,
    createCoreSite,
    currentUser,
    customAttributes,
    deleteCampusSites,
    fetchCampuses,
    fetchCoreSiteById,
    fetchSiteContacts,
    fuelTypes,
    history,
    navClients,
    selectedSite,
    setCoreSite,
    setCoreSites,
    siteContacts,
    sources,
    updateCoreSite,
    ...props
}) => {
    const [address, setAddress] = useState({});
    const [campusList, setCampusList] = useState([]);
    const [clientLoading, setClientLoading] = useState(false);
    const [address1, setAddress1] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [zip, setZip] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [originalCampusId, setOriginalCampusId] = useState(null);
    const [previousCampus, setPreviousCampus] = useState('');
    const [siteAttributes, setSiteAttributes] = useState([]);

    const [isAddNewCampusModalOpen, setIsAddNewCampusModalOpen] = useState(false);

    const queryValues = queryString.parse(history.location.search);

    useEffect(() => {
        return () => {
            const { pathname } = history.location;
            let updatedUrl = history.location.search.replace(/[?&]entityType=[^&]+/, '').replace(/^&/, '?');
            updatedUrl = updatedUrl.replace(/[?&]entityId=[^&]+/, '').replace(/^&/, '?');
            setCoreSite({});
            history.push({ pathname, search: updatedUrl });
        };
    }, []);

    useEffect(() => {
        if (!isEmpty(selectedSite)) {
            const fullTimeZone = selectedSite.timeZone ? TimezoneOffsets.find(tz => tz.name === selectedSite.timeZone) : null;

            let selectedClient = {};
            if (selectedSite.client) {
                selectedClient = selectedSite.client;
            } else {
                const selectedNavClient = navClients.find(item => item.id === queryValues.client);
                selectedClient = { ...selectedNavClient };
            }

            if (process.env.contactsEnabled) {
                fetchSiteContacts(selectedClient.id, selectedSite.id);
            }

            setOriginalCampusId(selectedSite.campus.id);

            formik.setFieldValue('name', selectedSite.name, false);
            formik.setFieldValue('buildingType', selectedSite.buildingType.id, false);
            formik.setFieldValue('addressSearch', selectedSite.address ? selectedSite.address.formattedAddress : null, false);
            const siteFuelTypes = selectedSite.fuelTypes || [];
            const fuelTypeList = siteFuelTypes.map(item => item.name);
            formik.setFieldValue('fuelTypes', fuelTypeList, false);
            formik.setFieldValue('squareFootage', selectedSite.squareFootage, false);
            formik.setFieldValue('client', selectedClient || {});
            formik.setFieldValue('salesContact', selectedSite.salesContact.id === EMPTY_GUID ? '' : selectedSite.salesContact.id, false);
            formik.setFieldValue('facilitiesContact', selectedSite.facilitiesContact.id === EMPTY_GUID ? '' : selectedSite.facilitiesContact.id, false);
            formik.setFieldValue('energyContact', selectedSite.energyContact.id === EMPTY_GUID ? '' : selectedSite.energyContact.id, false);
            formik.setFieldValue('timeZone', fullTimeZone, false);
            formik.setFieldValue('campus', selectedSite.campus.id && selectedSite.campus.id !== EMPTY_GUID ? selectedSite.campus.id : '', false);

            if (!isEmpty(selectedSite.customAttributes)) {
                selectedSite.customAttributes.forEach(item => {
                    const attributeName = item.customAvp.attributeName;
                    
                    switch (attributeName) {
                        case 'costFactor':
                            formik.setFieldValue('energyPrice', item.customAvp.attributeValue);
                            break;
                        //case 'energyPriceUnit':
                        //    formik.setFieldValue('energyPriceUnit', item.customAvp.attributeValue);
                        //    break;
                        case 'carbonRate':
                            formik.setFieldValue('energyEmission', item.customAvp.attributeValue);
                            break;
                        //case 'energyEmissionRateUnit':
                        //    formik.setFieldValue('energyEmissionRateUnit', item.customAvp.attributeValue);
                        //    break;
                        case 'energyTarget':
                            formik.setFieldValue('energyTargetValue', item.customAvp.attributeValue);
                            break;
                        default:
                            break;
                    }
                });
            }

        }
    }, [selectedSite]);

    useEffect(() => {
        if (activeNavClient.id && isEmpty(selectedSite)) formik.setFieldValue('client', activeNavClient || {});
    }, [activeNavClient]);

    useEffect(() => {
        const init = async () => {
            const queryValues = queryString.parse(history.location.search);
            if (queryValues.entityId) {
                if (isEmpty(selectedSite) || selectedSite.id !== queryValues.entityId) {
                    await fetchCoreSiteById(queryValues.entityId);
                }
                const promises = [fetchCampuses(queryValues.entityParent)];

                await Promise.all(promises.map(p => p.catch(err => err))).then(results => {
                    const campuses = results[0];
                    if (campuses) setCampusList(campuses);
                });
            }
        };

        if (activeNavCompany.id) init();
    }, [activeNavCompany]);

    useEffect(() => {
        if (!isEmpty(customAttributes)) {
            setSiteAttributes(customAttributes.Site);
        }
    }, [customAttributes]);

    const handleRoute = () => {
        const path = '/v2/dashboard/core/entities';

        if (history.location.state && history.location.state.returnUrl) {
            history.push(history.location.state.returnUrl);
        } else {
            history.push({ pathname: path, search: history.location.search });
        }
    };

    const handleTimeZoneChange = (event, timezone) => {
        formik.setFieldValue('timeZone', timezone);
    };

    const getClientId = () => {
        if (!isEmpty(navClients)) {
            return navClients.find(item => item.id === queryValues.entityParent);
        }
        else {
            return queryValues.entityParent;
        }
    };

    const formik = useFormik({
        initialValues: {
            client:             getClientId(),
            name:               '',
            buildingType:       '',
            squareFootage:      '',
            fuelTypes:          [],
            salesContact:       '',
            facilitiesContact:  '',
            primaryContact:     '',
            energyContact:      '',
            addressSearch:      '',
            timeZone:           '',
            campus:             '',
            energyPriceUnit:    '$/kWh',
            energyPrice:        '',
            energyEmissionUnit: 'Lbs/kWh',
            energyEmission:     ''
        },
        validationSchema:   ValidationSchema,
        enableReinitialize: true,
        onSubmit:           async (values, { setSubmitting }) => {
            let fuelTypeIds = fuelTypes.filter(f => formik.values.fuelTypes.includes(f.name)).map(type => type.id);
            const body = {
                name:           values.name,
                buildingTypeId: values.buildingType,
                squareFootage:  values.squareFootage,
                squareAreaUOM:  values.squareAreaUOM,
                timeZone:       values.timeZone ? values.timeZone.name : null,
                fuelTypes:      fuelTypeIds,
                address
            };

            if (process.env.contactsEnabled) {
                if (!isEmpty(values.facilitiesContact) && values.facilitiesContact !== EMPTY_GUID) body.facilitiesContactId = values.facilitiesContact;
                if (!isEmpty(values.salesContact) && values.salesContact !== EMPTY_GUID) body.salesContactId = values.salesContact;
                if (!isEmpty(values.energyContact) && values.energyContact !== EMPTY_GUID) body.energyContactId = values.energyContact;
            }

            if (!isEmpty(values.energyPrice)) {
                body.customAttributes = {};
                body.customAttributes.customAttributeValuePairs = [];

                const source = sources.find(item => item.name === 'K2A Core');

                const energyPriceAttribute = siteAttributes.find(item => item.name === 'costFactor');
                const energyEmissionRateAttribute = siteAttributes.find(item => item.name === 'carbonRate');
                const monthlyTargetAttribute = siteAttributes.find(item => item.name === 'energyTarget');

                const attributeValues = [];

                attributeValues.push({ sourceAvp: { avpId: source.id }, customAvp: { attributeId: energyPriceAttribute.id, attributeValue: values.energyPrice } });
                attributeValues.push({ sourceAvp: { avpId: source.id }, customAvp: { attributeId: energyEmissionRateAttribute.id, attributeValue: values.energyEmission } });
                attributeValues.push({ sourceAvp: { avpId: source.id }, customAvp: { attributeId: monthlyTargetAttribute.id, attributeValue: values.energyTargetValue } });

                body.customAttributes = { customAttributeValuePairs: [...attributeValues] };
            }


            const isUpdate = !isEmpty(queryValues.entityId);
            setIsLoading(true);
            let isCreated;
            try {
                if (isUpdate) {
                    isCreated = await updateCoreSite(queryValues.entityId, body);
                } else {
                    let campusId = '';
                    if (values.campus && values.campus !== EMPTY_GUID) {
                        campusId = values.campus;
                    }
                    isCreated = await createCoreSite(values.client.id, body, campusId);
                }
                setIsLoading(false);
                if (isCreated) {
                    if (values.campus !== originalCampusId) {
                        if (values.campus === EMPTY_GUID || !values.campus) {
                            if (!isEmpty(selectedSite.campus) && selectedSite.campus.id !== EMPTY_GUID) {
                                await deleteCampusSites(selectedSite.campus, [selectedSite], selectedSite.client);
                            }
                        } else {
                            if (values.campus && values.campus !== selectedSite.campus) {
                                if (selectedSite.campus && selectedSite.campus.id !== EMPTY_GUID) {
                                    await deleteCampusSites(selectedSite.campus, [selectedSite], selectedSite.client);
                                }
                                await createCampusSite(values.campus, [selectedSite.id], selectedSite.client.id);
                            }
                        }
                    }

                    setCoreSites([], {});
                    handleRoute();
                }
            } catch (error) {
                setIsLoading(false);
            }
        }
    });

    const handleGeocodeAddress = async address => {
        try {
            const results = await geocodeByAddress(address);
            const latLng = await getLatLng(results[0]);

            const streetNumber = results[0].address_components.find(result => {
                return result.types.includes('street_number');
            });

            const route = results[0].address_components.find(result => {
                return result.types.includes('route');
            });

            const city = results[0].address_components.find(result => {
                return result.types.includes('locality');
            });

            const subLocality = results[0].address_components.find(result => {
                return result.types.includes('sublocality');
            });

            const administrativeAreaLevel2 = results[0].address_components.find(result => {
                return result.types.includes('administrative_area_level_2');
            });

            const administrativeAreaLevel3 = results[0].address_components.find(result => {
                return result.types.includes('administrative_area_level_3');
            });

            const state = results[0].address_components.find(result => {
                return result.types.includes('administrative_area_level_1');
            });

            const zip = results[0].address_components.find(result => {
                return result.types.includes('postal_code');
            });

            const country = results[0].address_components.find(result => {
                return result.types.includes('country');
            });

            const address1Check = streetNumber ? `${streetNumber.long_name} ${route.long_name}` : route.long_name;
            const cityCheck = city ? city.long_name : administrativeAreaLevel3 ? administrativeAreaLevel3.long_name  : subLocality ? subLocality.long_name : '';

            formik.setFieldValue('addressSearch', results[0].formatted_address);

            setAddress1(address1Check);
            setCity(cityCheck);
            setState(state.short_name);
            setZip(zip.long_name);

            setAddress({
                latitude:                 get(latLng, 'lat', ''),
                longitude:                get(latLng, 'lng', ''),
                administrativeAreaLevel1: get(state, 'short_name', ''),
                administrativeAreaLevel2: get(administrativeAreaLevel2, 'long_name', ''),
                country:                  get(country, 'short_name', ''),
                locality:                 cityCheck,
                postalCode:               get(zip, 'long_name', ''),
                route:                    get(route, 'long_name', ''),
                streetNumber:             streetNumber ? streetNumber.long_name : '',
                formattedAddress:         get(results[0], 'formatted_address', '')
            });
        } catch (error) {
            // eslint-disable-next-line no-console
            console.warn('Error: ', error);
        }
    };

    if (!isEmpty(formik.values.addressSearch) && isEmpty(address1)) {
        handleGeocodeAddress(formik.values.addressSearch);
    }

    const handleClientChange = async (event, value) => {
        if (value) {
            setClientLoading(true);
            const promises = [fetchCampuses(value.id)];

            await Promise.all(promises.map(p => p.catch(err => err))).then(results => {
                const campuses = results[0];
                if (campuses) setCampusList(campuses);
            });

            formik.setFieldValue('client', value, true);
            setClientLoading(false);
        } else {
            formik.setFieldValue('client', '', true);
        }
    };

    const MenuProps = {
        disablePortal:      true,
        getContentAnchorEl: () => null,
        PaperProps:         {
            style: {
                maxHeight: 500
            },
        },
    };

    const handleClearField = field => {
        formik.setFieldValue(field, '');
    };

    const handleAddNewCampus = () => {
        setPreviousCampus(formik.values.campus);
        setIsAddNewCampusModalOpen(true);
    };

    const setSelectedCampus = id => {
        formik.setFieldValue('campus', id, true);
    };

    return (
        <div className={classes.ManageClientPage}>
            <div className={classes.ManagePageHeader}>
                <FormControl variant="outlined" size="small" className={classes.leftInputWrapper}>
                    <BobClientSelector
                        disabled={!isEmpty(selectedSite) || !isEmpty(activeNavClient) ? true : false}
                        companyId={activeNavCompany.id}
                        value={formik.values.client}
                        errors={formik.errors.client}
                        touched={formik.touched.client}
                        handleChange={handleClientChange}
                        label="Select Client"
                        required
                    />
                </FormControl>
            </div>
            <div className={classes.ManagePageTitle}>
                Site Details
            </div>
            <div className={classes.ManagePageBody}>
                <FormControl className={classes.leftInputWrapper}>
                    <TextField
                        id="name"
                        name="name"
                        type="text"
                        value={formik.values.name}
                        label={<span>Site Name<span className={classes.required}> *</span></span>}
                        placeholder="Enter"
                        size="small"
                        onChange={formik.handleChange}
                        InputLabelProps={{
                            classes: {
                                error:   classes.error,
                                focused: classes.labelFocused,
                                root:    classes.label
                            }
                        }}
                        InputProps={{
                            onBlur:  formik.handleBlur,
                            classes: {
                                error:     classes.cssErrorUnderline,
                                underline: classes.cssUnderline,
                                root:      classes.input
                            }
                        }}
                        variant="outlined"
                        FormHelperTextProps={{
                            classes: {
                                error: classes.error
                            }
                        }}
                        error={Boolean(formik.errors.name) && Boolean(formik.touched.name)}
                        helperText={Boolean(formik.touched.name) && formik.errors.name}
                    />
                </FormControl>
                <FormControl className={classes.rightInputWrapper}>
                    <AutoComplete
                        id="combo-box-timezones"
                        options={TimezoneOffsets}
                        getOptionLabel={(option) => option.name || ''}
                        value={formik.values.timeZone}
                        getOptionSelected={(option, value) => option.name === value.name}
                        onChange={handleTimeZoneChange}
                        size="small"
                        required
                        renderInput={(params) => 
                            <TextField 
                                {...params} 
                                label="Time Zone" 
                                variant="outlined" 
                                error={Boolean(formik.errors.timeZone) && Boolean(formik.touched.timeZone)}
                            />}
                    />
                </FormControl>
                <FormControl variant="outlined" size="small" className={classes.leftInputWrapper}>
                    <InputLabel
                        htmlFor="buildingType"
                        classes={{
                            error:   classes.error,
                            focused: classes.labelFocused,
                            root:    classes.label
                        }}
                        error={Boolean(formik.errors.buildingType) && Boolean(formik.touched.buildingType)}
                    >
                        {<span>Building Type<span className={classes.required}> *</span></span>}
                    </InputLabel>
                    <Select
                        value={formik.values.buildingType}
                        id="buildingType"
                        MenuProps={MenuProps}
                        onChange={formik.handleChange}
                        name="buildingType"
                        classes={{
                            root: classes.input
                        }}
                        inputProps={{
                            onBlur: formik.handleBlur
                        }}
                        error={Boolean(formik.errors.buildingType) && Boolean(formik.touched.buildingType)}
                    >
                        {
                            buildingTypes.map(type =>
                                <MenuItem
                                    key={type.id}
                                    value={type.id}
                                >
                                    {type.name}
                                </MenuItem>
                            )
                        }
                    </Select>
                    {formik.touched.buildingType &&
                        formik.errors.buildingType && <FormHelperText className={classes.error}>{formik.errors.buildingType}</FormHelperText>}
                </FormControl>
                <FormControl variant="outlined" size="small" className={classes.rightInputWrapper}>
                    <InputLabel
                        htmlFor="fuelTypes"
                        classes={{
                            error:   classes.error,
                            focused: classes.labelFocused,
                            root:    classes.label
                        }}
                        error={Boolean(formik.errors.fuelTypes) && Boolean(formik.touched.fuelTypes)}
                    >
                        {<span>Fuel Types</span>}
                    </InputLabel>
                    <Select
                        multiple
                        value={formik.values.fuelTypes}
                        id="fuelTypes"
                        MenuProps={MenuProps}
                        renderValue={selected => selected.join(', ')}
                        onChange={formik.handleChange}
                        name="fuelType"
                        className={`${classes.cssUnderline} ${classes.input}`}
                        inputProps={{
                            name: 'fuelTypes',
                            id:   'fuelTypes',
                        }}
                    >
                        {
                            fuelTypes.map(f => f.name).map((type, index) => (
                                <MenuItem
                                    className={classes.selectMenuItem}
                                    value={type}
                                    key={index}
                                >
                                    <Checkbox
                                        color="primary"
                                        checked={formik.values.fuelTypes.indexOf(type) > -1}
                                    />

                                    <span>{type}</span>
                                </MenuItem>
                            ))
                        }
                    </Select>
                </FormControl>
                <FormControl className={classes.squareFootageWrapper}>
                    <TextField
                        id="squareFootage"
                        name="squareFootage"
                        type="text"
                        value={formik.values.squareFootage}
                        label={<span>Square Footage<span className={classes.required}> *</span></span>}
                        placeholder="Enter"
                        size="small"
                        onChange={formik.handleChange}
                        InputLabelProps={{
                            classes: {
                                error:   classes.error,
                                focused: classes.labelFocused,
                                root:    classes.label
                            }
                        }}
                        InputProps={{
                            onBlur:  formik.handleBlur,
                            classes: {
                                error:     classes.cssErrorUnderline,
                                underline: classes.cssUnderline,
                                root:      classes.input
                            }
                        }}
                        variant="outlined"
                        FormHelperTextProps={{
                            classes: {
                                error: classes.error
                            }
                        }}
                        error={Boolean(formik.errors.squareFootage) && Boolean(formik.touched.squareFootage)}
                        helperText={Boolean(formik.touched.squareFootage) && formik.errors.squareFootage}
                    />
                </FormControl>
                {
                    !isEmpty(selectedSite) ?
                        <FormControl variant="outlined" size="small" className={classes.rightInputWrapper}>
                            <InputLabel
                                htmlFor="energyContact"
                                classes={{
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }}
                                error={Boolean(formik.errors.energyContact) && Boolean(formik.touched.energyContact)}
                            >
                                Energy Contact
                            </InputLabel>
                            <Select
                                value={formik.values.energyContact}
                                id="energyContact"
                                disabled={!process.env.contactsEnabled}
                                MenuProps={MenuProps}
                                onChange={formik.handleChange}
                                name="energyContact"
                                classes={{
                                    root: classes.input
                                }}
                                endAdornment={
                                    clientLoading ?
                                        <InputAdornment className={classes.selectAdornment} position="end">
                                            <CircularProgress size={15} />
                                        </InputAdornment>
                                        :
                                        !isEmpty(formik.values.energyContact) ?
                                            <InputAdornment position="end">
                                                <span role="none" onClick={() => handleClearField('energyContact')} className={clsx('material-icons-outlined md-18', classes.cancelIcon)}>
                                                    close
                                                </span>
                                            </InputAdornment>
                                            :
                                            null
                                }
                                inputProps={{
                                    onBlur: formik.handleBlur
                                }}
                                error={Boolean(formik.errors.energyContact) && Boolean(formik.touched.energyContact)}
                            >
                                {
                                    process.env.contactsEnabled ?
                                        siteContacts.map(type =>
                                            <MenuItem
                                                key={type.id}
                                                value={type.id}
                                            >
                                                {`${type.userName.FirstName} ${type.userName.LastName}`}
                                            </MenuItem>
                                        )
                                        :
                                        contacts.map(type =>
                                            <MenuItem
                                                key={type.id}
                                                value={type.id}
                                            >
                                                {type.name}
                                            </MenuItem>
                                        )
                                }
                            </Select>
                            {formik.touched.energyContact &&
                                formik.errors.energyContact && <FormHelperText className={classes.error}>{formik.errors.energyContact}</FormHelperText>}
                        </FormControl>
                        :
                        null
                }
                <PlacesAutocomplete
                    value={formik.values.addressSearch}
                    onChange={address => formik.setFieldValue('addressSearch', address)}
                    onSelect={(handleGeocodeAddress)}
                >
                    {({ getInputProps, suggestions, getSuggestionItemProps }) => (
                        <React.Fragment>
                            <FormControl variant="outlined" size="small" className={classes.leftInputWrapper}> 
                                <TextField
                                    {...getInputProps({
                                        name: 'addressSearch',
                                        id:   'addressSearch'
                                    })}
                                    label={<span>Search for Address<span className={classes.required}> *</span></span>}
                                    variant="outlined"
                                    size="small"
                                    classes={{
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }}
                                    error={Boolean(formik.errors.addressSearch) && Boolean(formik.touched.addressSearch)}
                                />
                                {formik.touched.addressSearch &&
                                    formik.errors.addressSearch && <FormHelperText className={classes.error}>{formik.errors.addressSearch}</FormHelperText>}
                            </FormControl>
                            {suggestions.length > 0 && <Paper className={classes.autoCompleteList}>
                                <List>
                                    {suggestions.map((suggestion, index) => {
                                        return (
                                            <div key={index}>
                                                <ListItem
                                                    button
                                                    className={classes.autoCompleteListItem}
                                                    {...getSuggestionItemProps(suggestion)}
                                                >
                                                    <ListItemText primary={suggestion.description} />
                                                </ListItem>
                                            </div>
                                        );
                                    })}
                                </List>
                                <GoogleLogo />
                            </Paper>}
                        </React.Fragment>
                    )}
                </PlacesAutocomplete>
                {
                    !isEmpty(selectedSite) ?
                        <React.Fragment>
                            <FormControl variant="outlined" size="small" className={classes.rightInputWrapper}>
                                <InputLabel
                                    htmlFor="facilitiesContact"
                                    classes={{
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }}
                                    error={Boolean(formik.errors.facilitiesContact) && Boolean(formik.touched.facilitiesContact)}
                                >
                                    Facilities Contact
                                </InputLabel>
                                <Select
                                    value={formik.values.facilitiesContact}
                                    id="facilitiesContact"
                                    disabled={!process.env.contactsEnabled}
                                    onChange={formik.handleChange}
                                    name="facilitiesContact"
                                    MenuProps={MenuProps}
                                    classes={{
                                        root: classes.input
                                    }}
                                    endAdornment={
                                        clientLoading ?
                                            <InputAdornment className={classes.selectAdornment} position="end">
                                                <CircularProgress size={15} />
                                            </InputAdornment>
                                            :
                                            !isEmpty(formik.values.facilitiesContact) ?
                                                <InputAdornment position="end">
                                                    <span role="none" onClick={() => handleClearField('facilitiesContact')} className={clsx('material-icons-outlined md-18', classes.cancelIcon)}>
                                                        close
                                                    </span>
                                                </InputAdornment>
                                                :
                                                null
                                    }
                                    inputProps={{
                                        onBlur: formik.handleBlur
                                    }}
                                    error={Boolean(formik.errors.facilitiesContact) && Boolean(formik.touched.facilitiesContact)}
                                >
                                    {
                                        process.env.contactsEnabled ?
                                            siteContacts.map(type =>
                                                <MenuItem
                                                    key={type.id}
                                                    value={type.id}
                                                >
                                                    {`${type.userName.FirstName} ${type.userName.LastName}`}
                                                </MenuItem>
                                            )
                                            :
                                            contacts.map(type =>
                                                <MenuItem
                                                    key={type.id}
                                                    value={type.id}
                                                >
                                                    {type.name}
                                                </MenuItem>
                                            )
                                    }
                                </Select>
                                {formik.touched.facilitiesContact &&
                                    formik.errors.facilitiesContact && <FormHelperText className={classes.error}>{formik.errors.facilitiesContact}</FormHelperText>}
                            </FormControl>
                            <FormControl variant="outlined" size="small" className={classes.salesContactWrapper}>
                                <InputLabel
                                    htmlFor="salesContact"
                                    classes={{
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }}
                                    error={Boolean(formik.errors.salesContact) && Boolean(formik.touched.salesContact)}
                                >
                                    Sales Contact
                                </InputLabel>
                                <Select
                                    value={formik.values.salesContact}
                                    id="salesContact"
                                    disabled={!process.env.contactsEnabled}
                                    MenuProps={MenuProps}
                                    onChange={formik.handleChange}
                                    name="salesContact"
                                    classes={{
                                        root: classes.input
                                    }}
                                    endAdornment={
                                        clientLoading ?
                                            <InputAdornment className={classes.selectAdornment} position="end">
                                                <CircularProgress size={15} />
                                            </InputAdornment>
                                            :
                                            !isEmpty(formik.values.salesContact) ?
                                                <InputAdornment position="end">
                                                    <span role="none" onClick={() => handleClearField('salesContact')} className={clsx('material-icons-outlined md-18', classes.cancelIcon)}>
                                                        close
                                                    </span>
                                                </InputAdornment>
                                                :
                                                null
                                    }
                                    inputProps={{
                                        onBlur: formik.handleBlur
                                    }}
                                    error={Boolean(formik.errors.salesContact) && Boolean(formik.touched.salesContact)}
                                >
                                    {
                                        process.env.contactsEnabled ?
                                            siteContacts.map(type =>
                                                <MenuItem
                                                    key={type.id}
                                                    value={type.id}
                                                >
                                                    {`${type.userName.FirstName} ${type.userName.LastName}`}
                                                </MenuItem>
                                            )
                                            :
                                            contacts.map(type =>
                                                <MenuItem
                                                    key={type.id}
                                                    value={type.id}
                                                >
                                                    {type.name}
                                                </MenuItem>
                                            )
                                    }
                                </Select>
                                {formik.touched.salesContact &&
                                    formik.errors.salesContact && <FormHelperText className={classes.error}>{formik.errors.salesContact}</FormHelperText>}
                            </FormControl>
                        </React.Fragment>
                        :
                        null
                }
                <div className={classes.HelperLine}>
                    <span className={clsx('material-icons-outlined md-18', classes.HelperIcon)}>
                        info
                    </span>
                    <span>Enter the physical address used to fill the address fields below.</span>
                </div>
                <FormControl className={classes.inputWrapper}>
                    <TextField
                        id="address1"
                        name="address1"
                        type="text"
                        value={address1}
                        label="Address"
                        disabled
                        size="small"
                        InputLabelProps={{
                            classes: {
                                error:   classes.error,
                                focused: classes.labelFocused,
                                root:    classes.label
                            }
                        }}
                        variant="outlined"
                        InputProps={{
                            classes: {
                                error:     classes.cssErrorUnderline,
                                underline: classes.cssUnderline,
                                root:      classes.input
                            }
                        }}
                    />
                </FormControl>
                <FormControl className={classes.inputWrapper}>
                    <TextField
                        id="city"
                        name="city"
                        type="text"
                        value={city}
                        label="City"
                        disabled
                        size="small"
                        InputLabelProps={{
                            classes: {
                                error:   classes.error,
                                focused: classes.labelFocused,
                                root:    classes.label
                            }
                        }}
                        variant="outlined"
                        InputProps={{
                            classes: {
                                error:     classes.cssErrorUnderline,
                                underline: classes.cssUnderline,
                                root:      classes.input
                            }
                        }}
                    />
                </FormControl>
                <div className={classes.formRow}>
                    <FormControl className={clsx(classes.inputWrapper, classes.state)}>
                        <TextField
                            id="state"
                            name="state"
                            type="text"
                            value={state}
                            label="State"
                            disabled
                            size="small"
                            InputLabelProps={{
                                classes: {
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }
                            }}
                            variant="outlined"
                            InputProps={{
                                classes: {
                                    error:     classes.cssErrorUnderline,
                                    underline: classes.cssUnderline,
                                    root:      classes.input
                                }
                            }}
                        />
                    </FormControl>
                    <FormControl className={clsx(classes.inputWrapper, classes.zip)}>
                        <TextField
                            id="zip"
                            name="zip"
                            type="text"
                            value={zip}
                            label="Zip Code"
                            disabled
                            size="small"
                            InputLabelProps={{
                                classes: {
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }
                            }}
                            variant="outlined"
                            InputProps={{
                                classes: {
                                    error:     classes.cssErrorUnderline,
                                    underline: classes.cssUnderline,
                                    root:      classes.input
                                }
                            }}
                        />
                    </FormControl>
                </div>
            </div>
            <div className={classes.ManagePageCampus}>
                <div className={classes.CampusTitle}>
                    Campus Details
                </div>
                <FormControl variant="outlined" size="small" className={classes.rightInputWrapper}>
                    <InputLabel
                        htmlFor="campus"
                        classes={{
                            error:   classes.error,
                            focused: classes.labelFocused,
                            root:    classes.label
                        }}
                        error={Boolean(formik.errors.campus) && Boolean(formik.touched.campus)}
                    >
                        Campus
                    </InputLabel>
                    <Select
                        value={formik.values.campus}
                        id="campus"
                        onChange={formik.handleChange}
                        name="campus"
                        classes={{
                            root: classes.input
                        }}
                        endAdornment={
                            clientLoading ?
                                <InputAdornment className={classes.selectAdornment} position="end">
                                    <CircularProgress size={15} />
                                </InputAdornment>
                                :
                                null
                        }
                        inputProps={{
                            onBlur: formik.handleBlur
                        }}
                        error={Boolean(formik.errors.campus) && Boolean(formik.touched.campus)}
                    >
                        <MenuItem
                            key={0}
                            value={''}
                            onClick={handleAddNewCampus}
                        >
                            <span className={clsx('material-icons-outlined md-18', classes.Icon)}>
                                add
                            </span>
                            Add New Campus
                        </MenuItem>
                        <MenuItem
                            key={1}
                            value=""
                        >
                            None
                        </MenuItem>
                        {
                            campusList.map(campus =>
                                <MenuItem
                                    key={campus.id}
                                    value={campus.id}
                                >
                                    {campus.name}
                                </MenuItem>
                            )
                        }
                    </Select>
                    {formik.touched.campus &&
                        formik.errors.campus && <FormHelperText className={classes.error}>{formik.errors.campus}</FormHelperText>}
                </FormControl>
            </div>
            <div className={classes.InfoPanel}>
                {
                    !isEmpty(currentUser.email) && (currentUser.isSuperUser || currentUser.email.toLowerCase().includes('@key2act.com')) ?
                        <SiteTabContainer
                            formik={formik}
                        />
                        :
                        null
                }
            </div>
            <div className={classes.ManagePageButtons}>
                <BobCancelButton
                    onClick={handleRoute}
                    disabled={formik.isSubmitting}
                    className={classes.CancelButton}
                    label={'Cancel'}
                />
                <BobConfirmButton
                    onClick={formik.handleSubmit}
                    disabled={formik.isSubmitting}
                    className={isLoading ? classes.DisabledButtons : classes.Buttons}
                    isLoading={isLoading}
                    label={'Save'}
                    iconSize={15}
                />
            </div>
            {
                isAddNewCampusModalOpen ?
                    <AddNewCampusModal
                        campusList={campusList}
                        client={formik.values.client}
                        previousCampus={previousCampus}
                        setCampusList={setCampusList}
                        setIsAddNewCampusModalOpen={setIsAddNewCampusModalOpen}
                        setSelectedCampus={setSelectedCampus}
                    />
                    :
                    null
            }
            {
                isEmpty(selectedSite) && queryValues.entityId ?
                    <BobLoadingMask />
                    :
                    null
            }
        </div>
    );
};

const mapStateToProps = state => {
    return {
        activeNavCompany: state.bob.activeNavCompany,
        activeNavClient:  state.bob.activeNavClient,
        buildingTypes:    state.architect.buildingTypes,
        contacts:         state.k2aCore.contacts,
        currentUser:      state.currentUser,
        customAttributes: state.attributeManagement.customAttributes,
        fuelTypes:        state.architect.fuelTypes,
        navClients:       state.bob.navClients,
        selectedSite:     state.k2aCore.selectedSite,
        siteContacts:     state.contact.siteContacts,
        sources:          state.attributeManagement.sources
    };
};

const mapDispatchToProps = {
    ...K2ACoreActions,
    ...ArchitectActions,
    ...ContactActions
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ManageSitePage));
